import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Users } from "lucide-react";

function computeElapsedMs(record, nowMs) {
  const checkIn = new Date(record.check_in_time).getTime();
  const breaks = record.breaks || [];
  let breakMs = 0;
  let referenceEnd = nowMs;
  for (const b of breaks) {
    if (b.end) breakMs += new Date(b.end).getTime() - new Date(b.start).getTime();
    else referenceEnd = new Date(b.start).getTime();
  }
  return Math.max(0, referenceEnd - checkIn - breakMs);
}

function formatDuration(ms) {
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function fmtMinutes(mins) {
  if (mins == null) return "—";
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}

const REFRESH_MS = 60 * 60 * 1000;

export default function TeamAttendanceOverview() {
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(Date.now());

  const load = () => {
    const today = new Date().toISOString().slice(0, 10);
    Promise.all([
      api.get("/attendance/team/today"),
      api.get("/schedule/team", { params: { date: today } }),
    ]).then(([attRes, schedRes]) => {
      const scheduleMap = Object.fromEntries(schedRes.data.map((s) => [s.employee_id, s.events]));
      setRows(attRes.data.map((a) => ({ ...a, events: scheduleMap[a.employee_id] || [] })));
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const refreshId = setInterval(load, REFRESH_MS);
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(refreshId); clearInterval(tickId); };
  }, []);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6" data-testid="team-attendance-overview">
      <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
        <Users className="h-4 w-4 text-[#00823B]" />
        <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Team Attendance Today</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-4 py-2.5 font-medium">Employee</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Time Worked</th>
              <th className="px-4 py-2.5 font-medium">Break</th>
              <th className="px-4 py-2.5 font-medium">Today's Schedule</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-[#4B5563]">No employees found.</td></tr>
            )}
            {rows.map((r) => {
              const rec = r.record;
              const isOpen = rec && !rec.check_out_time;
              const status = rec ? rec.status : "Not Checked In";
              const timeWorked = rec ? (isOpen ? formatDuration(computeElapsedMs(rec, now)) : fmtMinutes(rec.net_minutes)) : "—";
              return (
                <tr key={r.employee_id} data-testid={`team-attendance-row-${r.employee_id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                  <td className="px-4 py-2.5 font-medium text-[#1C1C1E]">{r.name}</td>
                  <td className="px-4 py-2.5"><StatusBadge value={status} /></td>
                  <td className="px-4 py-2.5 text-[#4B5563]">{timeWorked}</td>
                  <td className="px-4 py-2.5 text-[#4B5563]">{fmtMinutes(rec?.break_minutes)}</td>
                  <td className="px-4 py-2.5 text-[#4B5563] max-w-[260px] truncate" data-testid={`team-schedule-summary-${r.employee_id}`}>
                    {r.events.length === 0 ? "Nothing scheduled" : r.events.map((e) => `${e.start_time} ${e.title}`).join(", ")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
